import React, { useState, useEffect, useRef } from 'react';
import { Search, FileText, Calendar, Filter, User, Wallet, ArrowRight, UserPlus, Users, X, Monitor } from 'lucide-react';
import { useApp } from '../../../context/AppContext';
import apiService from '../../../services/apiService';
import SucursalFilter from '../../../components/filters/SucursalFilter';
import { formatMoney, formatDate } from '../../../utils/formatters';
import Modal from '../../../components/ui/Modal';
import DatosFiscalesForm from './DatosFiscalesForm';
import NuevaFacturaModal from './NuevaFacturaModal';
import AsignarClienteModal from './AsignarClienteModal';

// Función para obtener fechas de esta semana
const getThisWeekDates = () => {
  const today = new Date();
  const dayOfWeek = today.getDay();
  // Lunes de esta semana
  const monday = new Date(today);
  monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
  // Domingo de esta semana
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);

  const formatDate = (date) => date.toISOString().split('T')[0];

  return {
    inicio: formatDate(monday),
    fin: formatDate(sunday)
  };
};

const VentasFacturablesTab = ({ catalogos, onFacturaCreated }) => {
  const { showToast, loading, setLoading } = useApp();

  // Sub-tab activo
  const [activeSubTab, setActiveSubTab] = useState('cuenta'); // 'cuenta' o 'abono'
  const [sucursalId, setSucursalId] = useState('');

  // Obtener fechas de esta semana por defecto
  const thisWeek = getThisWeekDates();

  // Filtros para ventas
  const [filtros, setFiltros] = useState({
    fecha_inicio: thisWeek.inicio,
    fecha_fin: thisWeek.fin,
    cliente_id: '',
    folio: '',
    caja_id: ''
  });

  // Filtros para pagos
  const [filtrosPagos, setFiltrosPagos] = useState({
    fecha_inicio: thisWeek.inicio,
    fecha_fin: thisWeek.fin,
    cliente_id: '',
    folio: ''
  });

  // Datos
  const [clientes, setClientes] = useState([]);
  const [cajas, setCajas] = useState([]);

  // Buscador de clientes tipo Select2
  const [clienteSearchQuery, setClienteSearchQuery] = useState('');
  const [clienteSearchResults, setClienteSearchResults] = useState([]);
  const [showClienteDropdown, setShowClienteDropdown] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const clienteInputRef = useRef(null);
  const clienteDropdownRef = useRef(null);

  // Buscador de clientes para pagos
  const [clientePagosSearchQuery, setClientePagosSearchQuery] = useState('');
  const [clientePagosSearchResults, setClientePagosSearchResults] = useState([]);
  const [showClientePagosDropdown, setShowClientePagosDropdown] = useState(false);
  const [selectedClientePagos, setSelectedClientePagos] = useState(null);
  const clientePagosInputRef = useRef(null);
  const clientePagosDropdownRef = useRef(null);
  const [ventas, setVentas] = useState([]);
  const [ventasSeleccionadas, setVentasSeleccionadas] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [pagoSeleccionado, setPagoSeleccionado] = useState(null);

  // Cliente para facturar
  const [clienteParaFacturar, setClienteParaFacturar] = useState(null);
  const [datosFiscalesLista, setDatosFiscalesLista] = useState([]);
  const [datosFiscalesSeleccionado, setDatosFiscalesSeleccionado] = useState(null);

  // Emisores disponibles
  const [emisores, setEmisores] = useState([]);

  // Modales
  const [showDatosFiscalesModal, setShowDatosFiscalesModal] = useState(false);
  const [showFacturarModal, setShowFacturarModal] = useState(false);
  const [showFacturarPagoModal, setShowFacturarPagoModal] = useState(false);
  const [showAsignarClienteModal, setShowAsignarClienteModal] = useState(false);

  // Flag para saber si las ventas seleccionadas son de público en general
  const ventasPublicoGeneral = ventasSeleccionadas.length > 0 &&
    ventasSeleccionadas.every(v => !v.cliente_id || v.cliente_nombre === 'Público en General');

  // Cargar datos iniciales
  useEffect(() => {
    cargarClientes();
    cargarCajas();
    cargarEmisores();
  }, []);

  const cargarEmisores = async () => {
    try {
      const result = await apiService.getEmisores();
      if (result.success) setEmisores(result.data || []);
    } catch (error) {
      console.error('Error cargando emisores:', error);
    }
  };

  useEffect(() => {
    if (activeSubTab === 'cuenta') {
      cargarVentas();
    } else {
      cargarPagos();
    }
  }, [activeSubTab]);

  // Click outside handler para cerrar dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (clienteDropdownRef.current && !clienteDropdownRef.current.contains(event.target) &&
          clienteInputRef.current && !clienteInputRef.current.contains(event.target)) {
        setShowClienteDropdown(false);
      }
      if (clientePagosDropdownRef.current && !clientePagosDropdownRef.current.contains(event.target) &&
          clientePagosInputRef.current && !clientePagosInputRef.current.contains(event.target)) {
        setShowClientePagosDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const cargarClientes = async () => {
    try {
      const result = await apiService.getClientes();
      if (result.success) {
        setClientes(result.data);
      }
    } catch (error) {
      console.error('Error cargando clientes:', error);
    }
  };

  const cargarCajas = async () => {
    try {
      const result = await apiService.getCajas();
      if (result.success) {
        setCajas(result.data || []);
      }
    } catch (error) {
      console.error('Error cargando cajas:', error);
    }
  };

  // Buscar clientes por nombre (para ventas)
  const buscarClientes = async (query) => {
    if (!query || query.trim().length < 2) {
      setClienteSearchResults([]);
      return;
    }
    try {
      const result = await apiService.buscarClientes(query.trim());
      if (result.success && result.data) {
        setClienteSearchResults(result.data);
      } else {
        setClienteSearchResults([]);
      }
    } catch (error) {
      console.error('Error buscando clientes:', error);
      setClienteSearchResults([]);
    }
  };

  // Buscar clientes por nombre (para pagos)
  const buscarClientesPagos = async (query) => {
    if (!query || query.trim().length < 2) {
      setClientePagosSearchResults([]);
      return;
    }
    try {
      const result = await apiService.buscarClientes(query.trim());
      if (result.success && result.data) {
        setClientePagosSearchResults(result.data);
      } else {
        setClientePagosSearchResults([]);
      }
    } catch (error) {
      console.error('Error buscando clientes:', error);
      setClientePagosSearchResults([]);
    }
  };

  // Seleccionar cliente del dropdown (ventas)
  const handleSelectCliente = (cliente) => {
    setSelectedCliente(cliente);
    setFiltros(prev => ({ ...prev, cliente_id: cliente.id }));
    setClienteSearchQuery(cliente.nombre);
    setShowClienteDropdown(false);
    setClienteSearchResults([]);
  };

  // Limpiar cliente seleccionado (ventas)
  const handleClearCliente = () => {
    setSelectedCliente(null);
    setFiltros(prev => ({ ...prev, cliente_id: '' }));
    setClienteSearchQuery('');
    setClienteSearchResults([]);
  };

  // Seleccionar cliente del dropdown (pagos)
  const handleSelectClientePagos = (cliente) => {
    setSelectedClientePagos(cliente);
    setFiltrosPagos(prev => ({ ...prev, cliente_id: cliente.id }));
    setClientePagosSearchQuery(cliente.nombre);
    setShowClientePagosDropdown(false);
    setClientePagosSearchResults([]);
  };

  // Limpiar cliente seleccionado (pagos)
  const handleClearClientePagos = () => {
    setSelectedClientePagos(null);
    setFiltrosPagos(prev => ({ ...prev, cliente_id: '' }));
    setClientePagosSearchQuery('');
    setClientePagosSearchResults([]);
  };

  const cargarVentas = async () => {
    try {
      setLoading(true);
      const result = await apiService.getVentasFacturablesGeneral({ ...filtros, sucursal: sucursalId });
      if (result.success) {
        setVentas(result.data || []);
      } else {
        setVentas([]);
      }
    } catch (error) {
      console.error('Error cargando ventas:', error);
      showToast('Error al cargar ventas', 'error');
    } finally {
      setLoading(false);
    }
  };

  const cargarPagos = async () => {
    try {
      setLoading(true);
      const result = await apiService.getPagosClientes({
        ...filtrosPagos,
        facturado: 0,
        sucursal: sucursalId
      });
      if (result.success) {
        const data = result.data;
        if (Array.isArray(data)) {
          setPagos(data);
        } else if (data && Array.isArray(data.pagos)) {
          setPagos(data.pagos);
        } else {
          setPagos([]);
        }
      } else {
        setPagos([]);
      }
    } catch (error) {
      console.error('Error cargando pagos:', error);
      showToast('Error al cargar pagos', 'error');
      setPagos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (field, value) => {
    setFiltros(prev => ({ ...prev, [field]: value }));
  };

  const handleFiltroPagosChange = (field, value) => {
    setFiltrosPagos(prev => ({ ...prev, [field]: value }));
  };

  const handleBuscar = () => {
    setVentasSeleccionadas([]);
    setClienteParaFacturar(null);
    setDatosFiscalesLista([]); setDatosFiscalesSeleccionado(null);
    cargarVentas();
  };

  const handleBuscarPagos = () => {
    setPagoSeleccionado(null);
    setClienteParaFacturar(null);
    setDatosFiscalesLista([]); setDatosFiscalesSeleccionado(null);
    cargarPagos();
  };

  const handleLimpiarFiltros = () => {
    const week = getThisWeekDates();
    setFiltros({
      fecha_inicio: week.inicio,
      fecha_fin: week.fin,
      cliente_id: '',
      folio: '',
      caja_id: ''
    });
    setVentasSeleccionadas([]);
    setClienteParaFacturar(null);
    // Limpiar búsqueda de cliente
    setSelectedCliente(null);
    setClienteSearchQuery('');
    setClienteSearchResults([]);
  };

  const handleLimpiarFiltrosPagos = () => {
    const week = getThisWeekDates();
    setFiltrosPagos({
      fecha_inicio: week.inicio,
      fecha_fin: week.fin,
      cliente_id: '',
      folio: ''
    });
    setPagoSeleccionado(null);
    setClienteParaFacturar(null);
    // Limpiar búsqueda de cliente
    setSelectedClientePagos(null);
    setClientePagosSearchQuery('');
    setClientePagosSearchResults([]);
  };

  // Seleccionar/deseleccionar venta
  const handleToggleVenta = async (venta) => {
    const isSelected = ventasSeleccionadas.some(v => v.id === venta.id && v.tipo_venta === venta.tipo_venta);
    const esPublicoGeneral = !venta.cliente_id || venta.cliente_nombre === 'Público en General';

    if (isSelected) {
      const nuevasSeleccionadas = ventasSeleccionadas.filter(v => !(v.id === venta.id && v.tipo_venta === venta.tipo_venta));
      setVentasSeleccionadas(nuevasSeleccionadas);

      if (nuevasSeleccionadas.length === 0) {
        setClienteParaFacturar(null);
        setDatosFiscalesLista([]); setDatosFiscalesSeleccionado(null);
      }
    } else {
      // Si ya hay ventas seleccionadas, verificar compatibilidad
      if (ventasSeleccionadas.length > 0) {
        // No mezclar tipos de venta (mostrador con pedidos)
        if (ventasSeleccionadas[0].tipo_venta !== venta.tipo_venta) {
          showToast('No puede mezclar ventas de mostrador con pedidos', 'warning');
          return;
        }

        const primeraEsPublicoGeneral = !ventasSeleccionadas[0].cliente_id ||
          ventasSeleccionadas[0].cliente_nombre === 'Público en General';

        // Solo permitir agrupar ventas de público en general entre sí
        // O ventas del mismo cliente entre sí
        if (primeraEsPublicoGeneral !== esPublicoGeneral) {
          showToast('No puede mezclar ventas de público en general con ventas de clientes específicos', 'warning');
          return;
        }

        if (!primeraEsPublicoGeneral && ventasSeleccionadas[0].cliente_id !== venta.cliente_id) {
          showToast('Solo puede seleccionar ventas del mismo cliente', 'warning');
          return;
        }
      }

      setVentasSeleccionadas(prev => [...prev, venta]);

      // Si es la primera venta seleccionada y tiene cliente, cargar sus datos
      if (ventasSeleccionadas.length === 0 && !esPublicoGeneral) {
        await cargarDatosCliente(venta.cliente_id);
      }
    }
  };

  // Seleccionar pago
  const handleSelectPago = async (pago) => {
    if (pagoSeleccionado?.id === pago.id) {
      setPagoSeleccionado(null);
      setClienteParaFacturar(null);
      setDatosFiscalesLista([]); setDatosFiscalesSeleccionado(null);
    } else {
      setPagoSeleccionado(pago);
      await cargarDatosCliente(pago.cliente_id);
    }
  };

  // Seleccionar todas las ventas de un cliente
  const handleSelectAllDelCliente = () => {
    if (!filtros.cliente_id) {
      showToast('Seleccione un cliente en el filtro para seleccionar todas sus ventas', 'info');
      return;
    }

    const ventasDelCliente = ventas.filter(v => v.cliente_id === parseInt(filtros.cliente_id));

    if (ventasSeleccionadas.length === ventasDelCliente.length) {
      setVentasSeleccionadas([]);
      setClienteParaFacturar(null);
      setDatosFiscalesLista([]); setDatosFiscalesSeleccionado(null);
    } else {
      setVentasSeleccionadas(ventasDelCliente);
      if (ventasDelCliente.length > 0) {
        cargarDatosCliente(ventasDelCliente[0].cliente_id);
      }
    }
  };

  const cargarDatosCliente = async (clienteId) => {
    try {
      const cliente = clientes.find(c => c.id === clienteId);
      setClienteParaFacturar(cliente);

      // Usar datos_fiscales del cliente (ya cargados desde getClientes)
      const datosFiscales = cliente?.datos_fiscales || [];
      setDatosFiscalesLista(datosFiscales);

      // Seleccionar el principal por defecto, o el primero si no hay principal
      const principal = datosFiscales.find(df => df.es_principal) || datosFiscales[0] || null;
      setDatosFiscalesSeleccionado(principal);
    } catch (error) {
      console.error('Error cargando datos del cliente:', error);
    }
  };

  // Calcular totales de ventas
  const calcularTotales = () => {
    return {
      cantidad: ventasSeleccionadas.length,
      subtotal: ventasSeleccionadas.reduce((sum, v) => sum + parseFloat(v.subtotal || 0), 0),
      impuestos: ventasSeleccionadas.reduce((sum, v) => sum + parseFloat(v.impuesto || 0), 0),
      total: ventasSeleccionadas.reduce((sum, v) => sum + parseFloat(v.total || 0), 0)
    };
  };

  // Verificar si hay al menos un dato fiscal con datos completos
  const datosFiscalesCompletos = datosFiscalesSeleccionado &&
    datosFiscalesSeleccionado.rfc &&
    datosFiscalesSeleccionado.razon_social &&
    datosFiscalesSeleccionado.regimen_fiscal;

  const handleSiguientePaso = () => {
    if (activeSubTab === 'cuenta') {
      if (ventasSeleccionadas.length === 0) {
        showToast('Seleccione al menos una venta', 'warning');
        return;
      }

      // Si son ventas de público en general, primero hay que asignar cliente
      if (ventasPublicoGeneral && !clienteParaFacturar) {
        setShowAsignarClienteModal(true);
        return;
      }

      if (!datosFiscalesCompletos) {
        showToast('Complete los datos fiscales del cliente primero', 'warning');
        setShowDatosFiscalesModal(true);
        return;
      }
      setShowFacturarModal(true);
    } else {
      if (!pagoSeleccionado) {
        showToast('Seleccione un pago para facturar', 'warning');
        return;
      }
      if (!datosFiscalesCompletos) {
        showToast('Complete los datos fiscales del cliente primero', 'warning');
        setShowDatosFiscalesModal(true);
        return;
      }
      setShowFacturarPagoModal(true);
    }
  };

  // Handler cuando se asigna un cliente a ventas de público en general
  const handleClienteAsignado = async (cliente) => {
    setClienteParaFacturar(cliente);
    setShowAsignarClienteModal(false);

    // Guardar asignación en BD
    const ids = ventasSeleccionadas.map(v => v.id);
    const tipoVenta = ventasSeleccionadas[0]?.tipo_venta || 'venta';
    try {
      const result = await apiService.asignarClienteVentas(ids, cliente.id, tipoVenta);
      if (result.success) {
        // Actualizar ventas locales con el nuevo cliente
        setVentas(prev => prev.map(v =>
          ids.includes(v.id) ? { ...v, cliente_id: cliente.id, cliente_nombre: cliente.nombre } : v
        ));
        setVentasSeleccionadas(prev => prev.map(v => ({ ...v, cliente_id: cliente.id, cliente_nombre: cliente.nombre })));
      }
    } catch (error) {
      console.error('Error asignando cliente a ventas:', error);
    }

    // Cargar datos fiscales del cliente
    const datosFiscales = cliente?.datos_fiscales || [];
    setDatosFiscalesLista(datosFiscales);

    const principal = datosFiscales.find(df => df.es_principal) || datosFiscales[0] || null;
    setDatosFiscalesSeleccionado(principal);

    // Si no tiene datos fiscales completos, mostrar formulario
    if (!principal || !principal.rfc || !principal.razon_social || !principal.regimen_fiscal) {
      showToast('El cliente necesita datos fiscales para facturar', 'info');
      setShowDatosFiscalesModal(true);
    }
  };

  const handleDatosFiscalesGuardados = (datos) => {
    // Si se guardaron nuevos datos fiscales, agregarlos a la lista y seleccionarlos
    const nuevoDatoFiscal = {
      ...datos,
      es_principal: datosFiscalesLista.length === 0 ? 1 : 0
    };
    setDatosFiscalesLista(prev => [...prev, nuevoDatoFiscal]);
    setDatosFiscalesSeleccionado(nuevoDatoFiscal);
  };

  // Handler para cambiar la razón social seleccionada
  const handleCambiarDatoFiscal = (datoFiscalId) => {
    const seleccionado = datosFiscalesLista.find(df => df.id === parseInt(datoFiscalId));
    if (seleccionado) {
      setDatosFiscalesSeleccionado(seleccionado);
    }
  };

  const handleFacturaCreada = () => {
    setShowFacturarModal(false);
    setShowFacturarPagoModal(false);
    setVentasSeleccionadas([]);
    setPagoSeleccionado(null);
    setClienteParaFacturar(null);
    setDatosFiscalesLista([]); setDatosFiscalesSeleccionado(null);
    if (activeSubTab === 'cuenta') {
      cargarVentas();
    } else {
      cargarPagos();
    }
    showToast('Factura creada exitosamente', 'success');
    if (onFacturaCreated) {
      onFacturaCreated();
    }
  };

  const totales = calcularTotales();

  // Sub-tabs internos
  const subTabs = [
    { id: 'cuenta', label: 'Por Cuenta', icon: FileText },
    { id: 'abono', label: 'Por Abono', icon: Wallet }
  ];

  // Determinar si mostrar el boton de siguiente
  const mostrarBotonSiguiente = activeSubTab === 'cuenta'
    ? ventasSeleccionadas.length > 0
    : pagoSeleccionado !== null;

  return (
    <div className="ventas-facturables-container">
      {/* Sub-tabs */}
      <div className="sub-tabs-container">
        {subTabs.map(tab => (
          <button
            key={tab.id}
            className={`sub-tab-btn ${activeSubTab === tab.id ? 'active' : ''}`}
            onClick={() => {
              setActiveSubTab(tab.id);
              setVentasSeleccionadas([]);
              setPagoSeleccionado(null);
              setClienteParaFacturar(null);
              setDatosFiscalesLista([]); setDatosFiscalesSeleccionado(null);
            }}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab: Por Cuenta (Ventas) */}
      {activeSubTab === 'cuenta' && (
        <>
          {/* Filtros */}
          <div className="filtros-ventas">
            <div className="filtros-row">
              <div className="filtro-group">
                <label><Calendar size={14} /> Fecha Inicio</label>
                <input
                  type="date"
                  value={filtros.fecha_inicio}
                  onChange={(e) => handleFiltroChange('fecha_inicio', e.target.value)}
                />
              </div>

              <div className="filtro-group">
                <label><Calendar size={14} /> Fecha Fin</label>
                <input
                  type="date"
                  value={filtros.fecha_fin}
                  onChange={(e) => handleFiltroChange('fecha_fin', e.target.value)}
                />
              </div>

              <div className="filtro-group searchable-select-container">
                <label><User size={14} /> Cliente</label>
                <div className="searchable-select">
                  <div className="searchable-input-wrapper">
                    <input
                      ref={clienteInputRef}
                      type="text"
                      placeholder="Buscar cliente..."
                      value={clienteSearchQuery}
                      onChange={(e) => {
                        setClienteSearchQuery(e.target.value);
                        buscarClientes(e.target.value);
                        setShowClienteDropdown(true);
                        if (!e.target.value) {
                          setSelectedCliente(null);
                          setFiltros(prev => ({ ...prev, cliente_id: '' }));
                        }
                      }}
                      onFocus={() => {
                        if (clienteSearchQuery.length >= 2) {
                          setShowClienteDropdown(true);
                        }
                      }}
                    />
                    {selectedCliente && (
                      <button
                        type="button"
                        className="clear-selection-btn"
                        onClick={handleClearCliente}
                        title="Limpiar selección"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  {showClienteDropdown && clienteSearchResults.length > 0 && (
                    <div ref={clienteDropdownRef} className="searchable-dropdown">
                      {clienteSearchResults.map(cliente => (
                        <div
                          key={cliente.id}
                          className={`dropdown-item ${selectedCliente?.id === cliente.id ? 'selected' : ''}`}
                          onClick={() => handleSelectCliente(cliente)}
                        >
                          <span className="cliente-nombre">{cliente.nombre}</span>
                          {cliente.telefono && <span className="cliente-telefono">{cliente.telefono}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="filtro-group">
                <label><Monitor size={14} /> Caja</label>
                <select
                  value={filtros.caja_id}
                  onChange={(e) => handleFiltroChange('caja_id', e.target.value)}
                >
                  <option value="">Todas las cajas</option>
                  {cajas.map(caja => (
                    <option key={caja.id} value={caja.id}>{caja.identificador || caja.nombre_pc}</option>
                  ))}
                </select>
              </div>

              <div className="filtro-group">
                <label><FileText size={14} /> Folio</label>
                <input
                  type="text"
                  placeholder="Buscar folio..."
                  value={filtros.folio}
                  onChange={(e) => handleFiltroChange('folio', e.target.value)}
                />
              </div>

              <SucursalFilter value={sucursalId} onChange={setSucursalId} />

              <div className="filtro-actions">
                <button className="btn-buscar" onClick={handleBuscar}>
                  <Search size={16} />
                  Buscar
                </button>
                <button className="btn-limpiar" onClick={handleLimpiarFiltros}>
                  <Filter size={16} />
                  Limpiar
                </button>
              </div>
            </div>
          </div>

          {/* Contenido principal - Ventas (sin panel lateral) */}
          <div className="ventas-content-full">
            {loading ? (
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
                <p>Cargando ventas...</p>
              </div>
            ) : ventas.length === 0 ? (
              <div className="empty-state">
                <FileText size={48} />
                <h4>No hay ventas sin facturar</h4>
                <p>No se encontraron ventas pendientes de facturar con los filtros seleccionados</p>
              </div>
            ) : (
              <div className="ventas-table-wrapper">
                <table className="ventas-facturables-table">
                  <thead>
                    <tr>
                      <th className="checkbox-cell">
                        {filtros.cliente_id && (
                          <input
                            type="checkbox"
                            checked={ventasSeleccionadas.length === ventas.filter(v => v.cliente_id === parseInt(filtros.cliente_id)).length && ventas.length > 0}
                            onChange={handleSelectAllDelCliente}
                            title="Seleccionar todas del cliente"
                          />
                        )}
                      </th>
                      <th>Folio</th>
                      <th>Tipo</th>
                      <th>Fecha</th>
                      <th>Cliente</th>
                      <th>Productos</th>
                      <th className="text-right">Subtotal</th>
                      <th className="text-right">IVA</th>
                      <th className="text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ventas.map(venta => {
                      const isSelected = ventasSeleccionadas.some(v => v.id === venta.id && v.tipo_venta === venta.tipo_venta);
                      // Deshabilitar si: diferente cliente, diferente tipo de venta, o mezcla público/cliente
                      const primeraVenta = ventasSeleccionadas[0];
                      const esPublicoGeneral = !venta.cliente_id || venta.cliente_nombre === 'Público en General';
                      const primeraEsPublicoGeneral = primeraVenta && (!primeraVenta.cliente_id || primeraVenta.cliente_nombre === 'Público en General');
                      const isDisabled = ventasSeleccionadas.length > 0 && (
                        primeraVenta.tipo_venta !== venta.tipo_venta ||
                        (primeraEsPublicoGeneral !== esPublicoGeneral) ||
                        (!primeraEsPublicoGeneral && primeraVenta.cliente_id !== venta.cliente_id)
                      );

                      return (
                        <tr
                          key={`${venta.tipo_venta}-${venta.id}`}
                          className={`${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                        >
                          <td className="checkbox-cell">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleVenta(venta)}
                              disabled={isDisabled}
                            />
                          </td>
                          <td>
                            <span className="folio-badge">#{venta.folio || venta.id}</span>
                          </td>
                          <td>
                            <span className={`tipo-venta-badge ${venta.tipo_venta || 'venta'}`}>
                              {venta.tipo_venta === 'mostrador' ? 'Mostrador' : 'Pedido'}
                            </span>
                          </td>
                          <td>{formatDate(venta.fecha_venta)}</td>
                          <td>
                            <div className="cliente-cell">
                              <span className="cliente-nombre">{venta.cliente_nombre || 'Público en General'}</span>
                            </div>
                          </td>
                          <td className="text-center">{venta.total_productos}</td>
                          <td className="text-right">{formatMoney(venta.subtotal)}</td>
                          <td className="text-right">{formatMoney(venta.impuesto)}</td>
                          <td className="text-right total-cell">{formatMoney(venta.total)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Tab: Por Abono (Pagos) */}
      {activeSubTab === 'abono' && (
        <>
          {/* Filtros */}
          <div className="filtros-ventas">
            <div className="filtros-row">
              <div className="filtro-group">
                <label><Calendar size={14} /> Fecha Inicio</label>
                <input
                  type="date"
                  value={filtrosPagos.fecha_inicio}
                  onChange={(e) => handleFiltroPagosChange('fecha_inicio', e.target.value)}
                />
              </div>

              <div className="filtro-group">
                <label><Calendar size={14} /> Fecha Fin</label>
                <input
                  type="date"
                  value={filtrosPagos.fecha_fin}
                  onChange={(e) => handleFiltroPagosChange('fecha_fin', e.target.value)}
                />
              </div>

              <div className="filtro-group searchable-select-container">
                <label><User size={14} /> Cliente</label>
                <div className="searchable-select">
                  <div className="searchable-input-wrapper">
                    <input
                      ref={clientePagosInputRef}
                      type="text"
                      placeholder="Buscar cliente..."
                      value={clientePagosSearchQuery}
                      onChange={(e) => {
                        setClientePagosSearchQuery(e.target.value);
                        buscarClientesPagos(e.target.value);
                        setShowClientePagosDropdown(true);
                        if (!e.target.value) {
                          setSelectedClientePagos(null);
                          setFiltrosPagos(prev => ({ ...prev, cliente_id: '' }));
                        }
                      }}
                      onFocus={() => {
                        if (clientePagosSearchQuery.length >= 2) {
                          setShowClientePagosDropdown(true);
                        }
                      }}
                    />
                    {selectedClientePagos && (
                      <button
                        type="button"
                        className="clear-selection-btn"
                        onClick={handleClearClientePagos}
                        title="Limpiar selección"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  {showClientePagosDropdown && clientePagosSearchResults.length > 0 && (
                    <div ref={clientePagosDropdownRef} className="searchable-dropdown">
                      {clientePagosSearchResults.map(cliente => (
                        <div
                          key={cliente.id}
                          className={`dropdown-item ${selectedClientePagos?.id === cliente.id ? 'selected' : ''}`}
                          onClick={() => handleSelectClientePagos(cliente)}
                        >
                          <span className="cliente-nombre">{cliente.nombre}</span>
                          {cliente.telefono && <span className="cliente-telefono">{cliente.telefono}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="filtro-group">
                <label><Wallet size={14} /> Folio Pago</label>
                <input
                  type="text"
                  placeholder="Buscar folio..."
                  value={filtrosPagos.folio}
                  onChange={(e) => handleFiltroPagosChange('folio', e.target.value)}
                />
              </div>

              <SucursalFilter value={sucursalId} onChange={setSucursalId} />

              <div className="filtro-actions">
                <button className="btn-buscar" onClick={handleBuscarPagos}>
                  <Search size={16} />
                  Buscar
                </button>
                <button className="btn-limpiar" onClick={handleLimpiarFiltrosPagos}>
                  <Filter size={16} />
                  Limpiar
                </button>
              </div>
            </div>
          </div>

          {/* Contenido principal - Pagos (sin panel lateral) */}
          <div className="ventas-content-full">
            {loading ? (
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
                <p>Cargando pagos...</p>
              </div>
            ) : pagos.length === 0 ? (
              <div className="empty-state">
                <Wallet size={48} />
                <h4>No hay pagos sin facturar</h4>
                <p>No se encontraron abonos pendientes de facturar con los filtros seleccionados</p>
              </div>
            ) : (
              <div className="ventas-table-wrapper">
                <table className="ventas-facturables-table pagos-table">
                  <thead>
                    <tr>
                      <th className="checkbox-cell"></th>
                      <th>Folio Pago</th>
                      <th>Fecha</th>
                      <th>Cliente</th>
                      <th>Metodo</th>
                      <th className="text-center">Cuentas</th>
                      <th className="text-right">Monto Recibido</th>
                      <th className="text-right">Monto Aplicado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagos.map(pago => {
                      const isSelected = pagoSeleccionado?.id === pago.id;

                      return (
                        <tr
                          key={pago.id}
                          className={isSelected ? 'selected' : ''}
                          onClick={() => handleSelectPago(pago)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="checkbox-cell">
                            <input
                              type="radio"
                              checked={isSelected}
                              onChange={() => handleSelectPago(pago)}
                            />
                          </td>
                          <td>
                            <span className="folio-badge pago">{pago.folio}</span>
                          </td>
                          <td>{formatDate(pago.fecha_pago)}</td>
                          <td>
                            <div className="cliente-cell">
                              <span className="cliente-nombre">{pago.cliente_nombre}</span>
                            </div>
                          </td>
                          <td>
                            <span className={`metodo-badge ${pago.metodo_pago}`}>
                              {pago.metodo_pago}
                            </span>
                          </td>
                          <td className="text-center">
                            <span className="cuentas-badge">
                              {pago.cuentas_afectadas}
                              {pago.cuentas_liquidadas > 0 && (
                                <span className="liquidadas"> ({pago.cuentas_liquidadas} liq.)</span>
                              )}
                            </span>
                          </td>
                          <td className="text-right">{formatMoney(pago.monto_recibido)}</td>
                          <td className="text-right total-cell">{formatMoney(pago.monto_aplicado)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Barra inferior con boton Siguiente Paso */}
      {mostrarBotonSiguiente && (
        <div className="facturacion-footer-bar">
          <div className="footer-resumen">
            {activeSubTab === 'cuenta' ? (
              <>
                <span className="resumen-item">
                  <strong>{totales.cantidad}</strong> venta(s) seleccionada(s)
                </span>
                <span className="resumen-separator">|</span>
                {ventasPublicoGeneral ? (
                  clienteParaFacturar ? (
                    <span className="resumen-item">
                      <Users size={14} style={{ marginRight: 4 }} />
                      Facturar a: <strong>{clienteParaFacturar.nombre}</strong>
                      <button
                        className="btn-cambiar-cliente"
                        onClick={() => setShowAsignarClienteModal(true)}
                        style={{ marginLeft: 8, fontSize: '11px', padding: '2px 6px' }}
                      >
                        Cambiar
                      </button>
                    </span>
                  ) : (
                    <span className="resumen-item" style={{ color: '#f59e0b' }}>
                      <UserPlus size={14} style={{ marginRight: 4 }} />
                      Público en General - <strong>Asignar cliente para facturar</strong>
                    </span>
                  )
                ) : (
                  <span className="resumen-item">
                    Cliente: <strong>{clienteParaFacturar?.nombre}</strong>
                  </span>
                )}
                <span className="resumen-separator">|</span>
                <span className="resumen-item resumen-total">
                  Total: <strong>{formatMoney(totales.total)}</strong>
                </span>
              </>
            ) : (
              <>
                <span className="resumen-item">
                  Pago: <strong>{pagoSeleccionado?.folio}</strong>
                </span>
                <span className="resumen-separator">|</span>
                <span className="resumen-item">
                  Cliente: <strong>{clienteParaFacturar?.nombre}</strong>
                </span>
                <span className="resumen-separator">|</span>
                <span className="resumen-item resumen-total">
                  Monto: <strong>{formatMoney(pagoSeleccionado?.monto_aplicado)}</strong>
                </span>
              </>
            )}
          </div>
          <button
            className="btn-siguiente-paso"
            onClick={handleSiguientePaso}
            disabled={loading}
          >
            {ventasPublicoGeneral && !clienteParaFacturar ? 'Asignar Cliente' : 'Siguiente Paso'}
            <ArrowRight size={18} />
          </button>
        </div>
      )}

      {/* Modal de datos fiscales */}
      {showDatosFiscalesModal && clienteParaFacturar && (
        <DatosFiscalesForm
          cliente={clienteParaFacturar}
          catalogos={catalogos}
          onClose={() => setShowDatosFiscalesModal(false)}
          onSaved={handleDatosFiscalesGuardados}
        />
      )}

      {/* Modal de facturacion de ventas */}
      {showFacturarModal && clienteParaFacturar && (
        <NuevaFacturaModal
          cliente={clienteParaFacturar}
          datosFiscalesLista={datosFiscalesLista}
          datosFiscalesSeleccionado={datosFiscalesSeleccionado}
          onCambiarDatoFiscal={handleCambiarDatoFiscal}
          ventasIds={ventasSeleccionadas.map(v => v.id)}
          ventas={ventasSeleccionadas}
          tipoVenta={ventasSeleccionadas[0]?.tipo_venta || 'venta'}
          catalogos={catalogos}
          emisores={emisores}
          onClose={() => setShowFacturarModal(false)}
          onSuccess={handleFacturaCreada}
        />
      )}

      {/* Modal de facturacion de pago */}
      {showFacturarPagoModal && clienteParaFacturar && pagoSeleccionado && (
        <NuevaFacturaModal
          cliente={clienteParaFacturar}
          datosFiscalesLista={datosFiscalesLista}
          datosFiscalesSeleccionado={datosFiscalesSeleccionado}
          onCambiarDatoFiscal={handleCambiarDatoFiscal}
          pago={pagoSeleccionado}
          catalogos={catalogos}
          emisores={emisores}
          onClose={() => setShowFacturarPagoModal(false)}
          onSuccess={handleFacturaCreada}
          tipoPago={true}
        />
      )}

      {/* Modal para asignar cliente a ventas de público en general */}
      {showAsignarClienteModal && (
        <AsignarClienteModal
          clientes={clientes}
          catalogos={catalogos}
          onClose={() => setShowAsignarClienteModal(false)}
          onClienteSeleccionado={handleClienteAsignado}
          onClienteCreado={(nuevoCliente) => {
            // Agregar el nuevo cliente a la lista
            setClientes(prev => [...prev, nuevoCliente]);
            handleClienteAsignado(nuevoCliente);
          }}
        />
      )}
    </div>
  );
};

export default VentasFacturablesTab;
